/**
 * Selector de estancia por foto.
 *
 * Muestra:
 *  - Un dropdown compacto con las estancias del catálogo.
 *  - Opción "Otro (texto libre)" que abre un input.
 *  - Indica visualmente cuando la asignación es "IA" (autodetectada) vs "manual"
 *    (elegida por el perito). Al cambiar → PUT /api/photos/{id}/room.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Home, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const UNASSIGNED = "__none__";
const CUSTOM = "__custom__";

export function PhotoRoomSelector({ photo, catalog, onSaved }) {
  const current = photo?.room || null;
  const knownIds = new Set((catalog || []).map((r) => r.id));
  const isCustom = current && !knownIds.has(current);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customText, setCustomText] = useState(isCustom ? current : "");
  const [saving, setSaving] = useState(false);
  const source = photo?.room_source; // "manual" | "ai" | undefined

  useEffect(() => {
    setCustomText(isCustom ? current : "");
    setShowCustomInput(false);
  }, [photo?.id, current, isCustom]);

  const save = async (val) => {
    setSaving(true);
    try {
      await api.put(`/photos/${photo.id}/room`, { room: val });
      onSaved?.();
    } catch (err) {
      toast.error("No se pudo guardar la estancia: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const onChange = (val) => {
    if (val === CUSTOM) {
      setShowCustomInput(true);
      return;
    }
    if (val === UNASSIGNED) {
      save(null);
      return;
    }
    save(val);
  };

  const commitCustom = async () => {
    const t = customText.trim();
    if (!t) {
      setShowCustomInput(false);
      return;
    }
    await save(t);
    setShowCustomInput(false);
  };

  const selectValue = current
    ? (knownIds.has(current) ? current : CUSTOM)
    : UNASSIGNED;

  return (
    <div className="flex items-center gap-1.5" data-testid={`photo-room-selector-${photo.id}`}>
      <Home className="h-3 w-3 text-[#7A786F] shrink-0" />
      {showCustomInput ? (
        <>
          <input
            type="text"
            autoFocus
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitCustom();
              if (e.key === "Escape") { setShowCustomInput(false); setCustomText(isCustom ? current : ""); }
            }}
            onBlur={commitCustom}
            placeholder="Ej. vestidor, despacho…"
            maxLength={60}
            className="flex-1 h-6 text-[11px] px-1.5 rounded border border-[#D85A30] focus:outline-none focus:ring-1 focus:ring-[#D85A30]"
            data-testid={`photo-room-custom-input-${photo.id}`}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowCustomInput(false); setCustomText(isCustom ? current : ""); }}
            className="text-[10px] text-[#7A786F] hover:text-[#B43F3F]"
            title="Cancelar"
          >
            <X className="h-3 w-3" />
          </button>
        </>
      ) : (
        <>
          <Select value={selectValue} onValueChange={onChange} disabled={saving}>
            <SelectTrigger
              className="h-6 text-[11px] px-1.5 py-0 border-[#E5E2D8] rounded flex-1"
              data-testid={`photo-room-select-${photo.id}`}
            >
              <SelectValue placeholder="Sin asignar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED} className="text-xs">
                <span className="text-[#7A786F]">Sin asignar</span>
              </SelectItem>
              {(catalog || []).map((r) => (
                <SelectItem key={r.id} value={r.id} className="text-xs">
                  {r.label}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM} className="text-xs text-[#D85A30] font-medium">
                Otro (texto libre)…
              </SelectItem>
            </SelectContent>
          </Select>
          {source === "ai" && (
            <span
              className="text-[9px] font-semibold text-[#7A2A14] bg-[#FAECE7] px-1 py-0.5 rounded flex items-center gap-0.5"
              title="Estancia autodetectada por IA. Cámbiala si es incorrecta."
              data-testid={`photo-room-ai-badge-${photo.id}`}
            >
              <Sparkles className="h-2 w-2" /> IA
            </span>
          )}
        </>
      )}
    </div>
  );
}
